import { createNewUserForInterview } from '@modules/api';
import { addUserValidationSchema } from '@modules/user-utils';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormHelperText,
    Stack,
    TextField,
} from '@mui/material';
import { UserWithClaims, UserCreationForm } from '@synergetx/puppet-interview-types';
import { PermissionLevelDescription } from 'adminManagement/PermissionLevelDescription';
import UserFormAdminSelect from 'adminManagement/UserFormAdminSelect';
import { Field, Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { memo } from 'react';
import { toast } from 'react-toastify';
import { userMessage } from '@modules/api-error';
import { useInterviewOptions } from '@providers/InterviewsProvider';

interface Props {
    userToEdit: UserCreationForm;
    setUserToEdit: React.Dispatch<React.SetStateAction<UserCreationForm | undefined>>;
    setUsers: React.Dispatch<React.SetStateAction<UserWithClaims[] | null>>;
}

const CreateUserDialog = ({ userToEdit, setUserToEdit, setUsers }: Props) => {
    const { selectedInterview } = useInterviewOptions();
    const groupId = selectedInterview?.groupId;

    const onSubmit = async (
        userInfo: UserCreationForm,
        { setSubmitting }: FormikHelpers<UserCreationForm>,
    ) => {
        if (!groupId) {
            toast.error('No project group is selected.');
            return;
        }

        try {
            // Keep the dialog open until the write lands, so a failure leaves the entered
            // details on screen to correct rather than discarding them.
            const { user } = await toast.promise(
                createNewUserForInterview(groupId, userInfo.claims, userInfo),
                {
                    error: { render: ({ data }) => userMessage(data, 'Error creating user') },
                    pending: `Creating user...`,
                    success: 'Success!',
                },
            );

            if (user) {
                setUsers((prev) => (prev || []).filter(({ id }) => id !== user.id).concat([user]));
            }

            setUserToEdit(undefined);
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <Dialog maxWidth="md" fullWidth open onClose={() => setUserToEdit(undefined)}>
            <Formik
                enableReinitialize
                initialValues={userToEdit}
                onSubmit={onSubmit}
                validateOnChange={true}
                validationSchema={addUserValidationSchema}
            >
                {({ dirty, isValid, isSubmitting, values, errors }) => (
                    <Form>
                        <DialogTitle>
                            {values.id ? 'Edit Existing User' : 'Create New User'}
                        </DialogTitle>
                        <DialogContent dividers>
                            <DialogContentText sx={{ marginBottom: '1rem' }}>
                                Please enter the name and email of the user to create
                            </DialogContentText>
                            <Stack spacing={2}>
                                <Box>
                                    <Field
                                        name="name"
                                        label="Name"
                                        as={TextField}
                                        fullWidth
                                        required
                                        variant="outlined"
                                    />
                                </Box>
                                <Box>
                                    <Field
                                        name="email"
                                        label="Email"
                                        as={TextField}
                                        fullWidth
                                        required
                                        variant="outlined"
                                    />
                                </Box>
                                {values.id ? null : (
                                    <Box>
                                        <DialogContentText>
                                            Password for new user: {values.email.split('@')[0]}
                                            Password1!
                                        </DialogContentText>
                                    </Box>
                                )}
                                <UserFormAdminSelect />
                                <Box>
                                    <PermissionLevelDescription />
                                </Box>
                            </Stack>
                            {errors && Object.keys(errors).length
                                ? Object.keys(errors).map((k) => {
                                      // `claims` is a nested object, so FormikErrors types its
                                      // entry as an object rather than a message string.
                                      const message =
                                          errors[k as keyof FormikErrors<UserCreationForm>];
                                      return (
                                          <FormHelperText sx={{ marginTop: '1rem' }} key={k} error>
                                              {typeof message === 'string' ? message : null}
                                          </FormHelperText>
                                      );
                                  })
                                : null}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="outlined"
                                color="secondary"
                                disabled={isSubmitting}
                                onClick={() => setUserToEdit(undefined)}
                            >
                                Close
                            </Button>
                            {/* `type="submit"` so Formik owns the submit: it revalidates first
                                and tracks `isSubmitting`, which is what blocks a double-submit.
                                Calling `onSubmit(values)` from onClick bypassed all of that. */}
                            <Button
                                type="submit"
                                variant="contained"
                                loading={isSubmitting}
                                disabled={!dirty || !isValid}
                            >
                                Submit
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default memo(CreateUserDialog);
